import { Flex, FlexProps, SystemStyleObject, useMergeRefs } from '@chakra-ui/react';
import { useBreakpointValue } from '@ifixit/ui/hooks/use-media-query';
import { useRef, useEffect, useState } from 'react';
import type { Ref } from 'react';
import { easeInOutQuad } from './easing';

type BeforeAfterOpacityState = {
   after: SystemStyleObject;
   before: SystemStyleObject;
};

const gradientWidths = { base: 40, sm: 50, md: 70, lg: 100 };

const sharedStyle: SystemStyleObject = {
   content: '""',
   position: 'absolute',
   height: '100%',
   pointerEvents: 'none',
   zIndex: 9999,
};

const beforeStyle: SystemStyleObject = {
   ...sharedStyle,
   left: '-1px',
   background: 'linear-gradient(270deg, rgba(249, 250, 251, 0) 0%, #F9FAFB 106.41%)',
};

const afterStyle: SystemStyleObject = {
   ...sharedStyle,
   right: '-1px',
   background: 'linear-gradient(90deg, rgba(249, 250, 251, 0) 0%, #F9FAFB 106.41%)',
};

function getGradients(): {
   before: SystemStyleObject;
   after: SystemStyleObject;
   gradientWidthPX: number;
} {
   // eslint-disable-next-line react-hooks/rules-of-hooks -- FIXME: React Hook "useBreakpointValue" is called in "getGradients" that is neither a React function component nor a custom Hook ...
   const gradientWidthPX = useBreakpointValue(gradientWidths);
   return {
      before: { ...beforeStyle, width: `${gradientWidthPX}px` },
      after: { ...afterStyle, width: `${gradientWidthPX}px` },
      // @ts-expect-error TS(2322) FIXME: Type 'number | undefined' is not assignable to typ... Remove this comment to see the full error message
      gradientWidthPX,
   };
}

export function FlexScrollGradient({ ref, ...props }: FlexProps & { ref?: Ref<HTMLDivElement> }) {
   const internalRef = useRef<HTMLDivElement>(undefined);
   const refs = useMergeRefs(internalRef, ref);

   const [flexScrollState, setFlexScrollState] = useState<BeforeAfterOpacityState>({
      before: { opacity: 0 },
      after: { opacity: 0 },
   });

   const { before, after, gradientWidthPX } = getGradients();
   const doMeasure = (el: HTMLElement) => {
      const { scrollWidth, clientWidth, scrollLeft, clientHeight } = el;

      const isScrollable = scrollWidth > clientWidth;
      if (!isScrollable) {
         setFlexScrollState(() => ({
            before: { opacity: 0, height: 0 },
            after: { opacity: 0, height: 0 },
         }));
         return;
      }

      const scrollPosition = scrollLeft + clientWidth;
      const afterStart = scrollWidth - gradientWidthPX;
      const height = `${clientHeight}px`;

      const beforeGradientShownPercent =
         scrollLeft < gradientWidthPX ? scrollLeft / gradientWidthPX : 1;

      const afterGradientShownPercent =
         scrollPosition >= afterStart ? 1 - (scrollPosition - afterStart) / gradientWidthPX : 1;

      const beforeGradientOpacity = easeInOutQuad(Math.max(0, beforeGradientShownPercent));
      const afterGradientOpacity = easeInOutQuad(Math.max(0, afterGradientShownPercent));

      setFlexScrollState(() => ({
         before: { opacity: beforeGradientOpacity, height },
         after: { opacity: afterGradientOpacity, height },
      }));
   };

   useEffect(() => {
      if (!internalRef.current) {
         return;
      }

      const element = internalRef.current;

      const measure = () => {
         doMeasure(element);
      };

      // Use ResizeObserver for size changes instead of reading on every render
      const resizeObserver = new ResizeObserver(measure);
      resizeObserver.observe(element);

      element.addEventListener('scroll', measure);

      return () => {
         resizeObserver.disconnect();
         element.removeEventListener('scroll', measure);
      };
   }, [gradientWidthPX]);

   const newBefore = { ...before, ...flexScrollState.before };
   const newAfter = { ...after, ...flexScrollState.after };

   return (
      <Flex {...props} position="relative" isolation="isolate">
         <Flex
            ref={refs}
            overflow="auto"
            _before={newBefore}
            _after={newAfter}
            sx={{ ...props.sx }}
         >
            {props.children}
         </Flex>
      </Flex>
   );
}
